import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '@shared/atoms/Button';
import T from '@shared/atoms/Typography';
import Tooltip from '@shared/atoms/Tooltip';
import theme from '@shared/themes/default';

import { courseStatus, DOMAIN_LOCALES, PROCESSING_STATUS } from '@shared/const';
import { newSelectTranslation, useForm } from '@shared/utils';

import { useCourse } from '../../../../contexts/CourseContext';
import { useAppGlobals } from '../../../../contexts/AppContext';

import UploadWithList from '../Shared/UploadWithList';
import AttachmentDrawer from './components/AttachmentDrawer';
import ValidationList from '../../components/ValidationList';

const SectionStyled = styled.section`
  background: ${theme.palette.darkWrapper};
  padding: ${theme.spacing.s};
`;

const HeaderStyled = styled.header`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: ${theme.spacing.s};
`;

const TitleWrapper = styled.div`
  align-items: center;
  display: flex;
  column-gap: ${theme.spacing.xl};
`;

const ContentWrapper = styled.div`
  width: 30%;
  padding-bottom: ${theme.spacing.xl};
`;

const Attachments = () => {
  const { t } = useTranslation();
  const { domain } = useAppGlobals();
  const locales = DOMAIN_LOCALES[domain];

  const {
    course,
    hasChanged,
    locked,
    saveAttachments,
    setHasChanged,
    setHandleSave,
    setUploadingFiles,
    submitting,
    uploadingFiles,
    validationDetails,
  } = useCourse();

  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const isPublished = course?.status === courseStatus.PUBLISHED;

  const { handleChange, handleSubmit, setDefaults, values } = useForm({
    fields: {
      attachments: {},
    },
    onSubmit: saveAttachments,
  });

  useEffect(() => {
    setDefaults({
      attachments: course?.attachments || [],
    });
  }, [course]);

  useEffect(() => {
    setHandleSave(() => handleSubmit);
  }, [values]);

  const attachments = values?.attachments || [];

  const handleModify = (index) => {
    setSelectedIndex(index);
    setOpen(true);
  };

  const handleDelete = (index) => {
    handleChange({
      name: 'attachments',
      value: attachments.filter((_, idx) => index !== idx),
    });
    setHasChanged(true);
  };

  const handleAddFiles = (files) => {
    handleChange({
      name: 'attachments',
      value: [
        ...attachments,
        ...files.map((file) => ({
          name: locales.map((locale) => ({
            languageCode: locale,
            value: file.name,
          })),
          file: {
            key: file.s3File.key,
            processingStatus: PROCESSING_STATUS.TO_SCHEDULE,
          },
        })),
      ],
    });
    setHasChanged(true);
    setUploadingFiles(false);
  };

  const onCloseDrawer = () => {
    setOpen(false);
    setSelectedIndex(-1);
  };

  const onSaveDrawer = ({ name, ext }) => {
    const newAttachments = [...attachments];
    newAttachments[selectedIndex] = {
      ...newAttachments[selectedIndex],
      name: name.map((translation) => ({
        languageCode: translation.languageCode,
        value: [translation.value, ext].join('.'),
      })),
    };
    if (
      newAttachments[selectedIndex]?.file?.processingStatus !==
      PROCESSING_STATUS.TO_SCHEDULE
    ) {
      newAttachments[selectedIndex].updated = true;
    }
    handleChange({ name: 'attachments', value: newAttachments });
    setOpen(false);
    setHasChanged(true);
  };

  const onStartUpload = () => {
    if (!hasChanged) setHasChanged(true);
    setUploadingFiles(true);
  };

  const listItemsActions = [
    {
      label: t('backoffice.courses.attachmentsTab.modifyFilename'),
      handler: (idx) => handleModify(Number.parseInt(idx, 10)),
    },
    {
      label: t('backoffice.courses.attachmentsTab.deleteFilename'),
      handler: (idx) => handleDelete(Number.parseInt(idx, 10)),
    },
  ];

  const saveDisabled =
    !hasChanged || submitting || (isPublished && locked) || uploadingFiles;

  // TODO: Remove this mapping when lessons tab drawer is integrated
  const mappedAttachments = (values?.attachments || []).map((attachment) => ({
    ...attachment,
    key: newSelectTranslation(attachment.name),
    name: newSelectTranslation(attachment.name),
    processingStatus: attachment.file?.processingStatus,
  }));

  return (
    <SectionStyled>
      <HeaderStyled>
        <TitleWrapper>
          <T
            color={theme.palette.lightPure}
            fontWeight="semibold"
            variant="paragraph"
          >
            {t('backoffice.courses.attachmentsTab.title')}
          </T>
        </TitleWrapper>
        <Tooltip
          arrow
          title={
            course.status === courseStatus.PUBLISHED &&
            saveDisabled &&
            !submitting &&
            !locked &&
            Object.keys(validationDetails)?.length ? (
              <>
                <T color={theme.palette.mainDark}>
                  {t('backoffice.courses.publishing.save')}
                </T>
                <ValidationList details={validationDetails} />
              </>
            ) : (
              ''
            )
          }
        >
          <div>
            <Button
              disabled={saveDisabled}
              loading={submitting}
              onClick={handleSubmit}
              type="secondary"
            >
              {t('generic.save')}
            </Button>
          </div>
        </Tooltip>
      </HeaderStyled>
      <ContentWrapper>
        <UploadWithList
          actions={listItemsActions}
          deleteFiles
          description={[
            t('backoffice.courses.attachmentsTab.uploadDescription'),
          ]}
          items={mappedAttachments}
          onStartUpload={onStartUpload}
          onUpload={handleAddFiles}
          uploadHeight="124px"
          disabled={locked}
        />
        <AttachmentDrawer
          attachment={attachments[selectedIndex]}
          onClose={onCloseDrawer}
          onSave={onSaveDrawer}
          open={open}
        />
      </ContentWrapper>
    </SectionStyled>
  );
};

export default Attachments;
